import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Country, State, City } from "country-state-city";
import { useDispatch } from "react-redux";
import useAuth from "../../../../hooks/useAuth";
import { apiGatewayAxiosInstance } from "../../../../utils/axios";
import AlertMessageComponent from "../../../components/AlertMessageComponent";
import FetchingSpinner from "../../../components/FetchingSpinner";
import { validateTextField } from "../../../../utils/validateTextFiled";
import SearchableDropdown from "../../../mis/SearchableDropdown";
import SuccessMessageComponent from "../../../components/SuccessMessageComponent";

export default function AddSuperAdmin({ setShowAddSuperAdminForm, setOperationStatus }) {
    const dispatch = useDispatch();
    const user = useAuth();

    const [errorMessage, setErrorMessage] = useState("");
    const [password, setPassword] = useState("");
    const [copySuccess, setCopySuccess] = useState(false);
    const [countriesList, setCountriesList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [countryShortCode, setCountryShortCode] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        countryName: "",
        state: "",
        city: "",
        countryCode: "",
        mobileNumber: "",
        createdByName: user.user.name,
        createdById: user.user._id,
        password: "",
        userName: "",
    });

    const [errors, setErrors] = useState({});

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleEmailAndUserName = e => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            email: value,
            userName: value,
        }));
        setErrors(prev => ({ ...prev, email: "" }));
    };

    const handleSelectCountry = e => {
        const isoCode = e.target.value;
        const selectedCountry = countriesList.find(c => c.isoCode === isoCode);
        if (!selectedCountry) return;

        const states = State.getStatesOfCountry(isoCode);

        setCountryShortCode(isoCode);
        setFormData(prev => ({
            ...prev,
            countryName: isoCode,
            countryCode: selectedCountry.phonecode,
            state: "",
            city: "",
        }));
        setStateList(states);
        setCityList([]);
        setErrors(prev => ({ ...prev, countryName: "", countryCode: "" }));
    };

    const handleSelectState = e => {
        const isoCode = e.target.value;
        const selectedState = stateList.find(s => s.isoCode === isoCode);
        if (!selectedState) return;

        const cities = City.getCitiesOfState(countryShortCode, isoCode);
        setFormData(prev => ({
            ...prev,
            state: isoCode,
            city: "",
        }));
        setCityList(cities);
        setErrors(prev => ({ ...prev, state: "" }));
    };

    const onChangeMobileNumber = e => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setFormData(prev => ({ ...prev, mobileNumber: value }));
            setErrors(prev => ({ ...prev, mobileNumber: "" }));
        }
    };

    const validateAllFields = () => {
        const newErrors = {};
        let valid = true;

        const { isValid: isNameValid, error: nameError } = validateTextField(formData.name, 3, 200, "SuperAdmin Name");
        if (!isNameValid) {
            newErrors.name = nameError;
            valid = false;
        }

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Invalid email format";
            valid = false;
        }

        if (!formData.countryName) {
            newErrors.countryName = "Country is required";
            valid = false;
        }

        if (!formData.state) {
            newErrors.state = "State is required";
            valid = false;
        }

        if (!formData.city) {
            newErrors.city = "City is required";
            valid = false;
        }

        if (!formData.countryCode) {
            newErrors.countryCode = "Code is required";
            valid = false;
        }

        if (!formData.password) {
            newErrors.password = "Password is required";
            valid = false;
        }

        const phone = formData.mobileNumber.trim();
        if (!phone) {
            newErrors.mobileNumber = "Mobile Number is required";
            valid = false;
        } else if (!/^\d{7,15}$/.test(phone)) {
            newErrors.mobileNumber = "Mobile Number must be 7 to 15 digits";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleGeneratePassword = async () => {
        try {
            const res = await apiGatewayAxiosInstance.post("/user_management/clients/generate-password");
            const newPass = res.data.password;
            setPassword(newPass);
            setFormData(prev => ({ ...prev, password: newPass }));
            setErrorMessage("");
        } catch (err) {
            setErrorMessage(err.response?.data?.message || "Server not reachable.");
        }
    };

    const handleCopyPassword = () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 1500);
    };

    const handleCreateSuperAdmin = async () => {
        if (!validateAllFields()) return;
        try {
            setIsLoading(true);
            formData.email = formData.email.trim();
            formData.mobileNumber = formData.mobileNumber.trim();
            await apiGatewayAxiosInstance.post("/user_management/super-admin/create-super-admin", formData);
            setOperationStatus({
                message: "Super Admin Created Successfuly.",
                success: true,
                failed: false,
            });
            setShowAddSuperAdminForm(false);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || "Server not reachable.");
            setIsLoading(false);
        }
    };

    const onClickCancelButton = () => {
        dispatch(setShowAddSuperAdminForm(false));
    };

    const getCountriesName = () => {
        const countries = Country.getAllCountries();
        setCountriesList(countries);
    };

    useEffect(() => {
        getCountriesName();
    }, []);

    return isLoading ? (
        <FetchingSpinner />
    ) : (
        <>
            {isLoading ? (
                <FetchingSpinner />
            ) : (
                <form>
                    <Grid item md={6} display={"flex"} alignItems={"center"}>
                        <IconButton color="secondary" onClick={onClickCancelButton} size="medium">
                            <ArrowBackIosIcon sx={{ cursor: "pointer" }} />
                        </IconButton>

                        <Grid role="presentation">
                            <Breadcrumbs aria-label="breadcrumb" fontSize="large">
                                <Link underline="hover" color="inherit" className="breadcrumbItem breadcrumbFontSize" onClick={onClickCancelButton}>
                                    Super Admin
                                </Link>

                                <Typography className="breadcrumbItem" color="text.primary">
                                    Add New Super Admin
                                </Typography>
                            </Breadcrumbs>
                        </Grid>
                    </Grid>
                    <Card sx={{ padding: "0.8rem" }}>
                        <CardContent sx={{ padding: "10px" }}>
                            <Grid className="d-flex" gap={5}>
                                <FormControl fullWidth className="field-spacing" error={Boolean(errors.name)}>
                                    <TextField
                                        name="name"
                                        fullWidth
                                        required
                                        label={<span>Super Admin Name</span>}
                                        id="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        error={Boolean(errors.name)}
                                        helperText={errors.name}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </FormControl>
                                <FormControl fullWidth className="field-spacing" error={Boolean(errors.email)}>
                                    <TextField
                                        name="email"
                                        fullWidth
                                        required
                                        label={<span>Email</span>}
                                        id="email"
                                        value={formData.email}
                                        onChange={handleEmailAndUserName}
                                        error={Boolean(errors.email)}
                                        helperText={errors.email}
                                        inputProps={{ maxLength: 320 }}
                                    />
                                </FormControl>
                            </Grid>

                            <Box sx={{ width: "100%" }} display="flex" gap={5} className="field-spacing">
                                <Grid width="49%" container sx={{ justifyContent: "space-between" }}>
                                    <Grid item md={3.9}>
                                        <FormControl fullWidth error={Boolean(errors.countryName)}>
                                            {/* <InputLabel>Country</InputLabel>
                                            <Select value={formData.countryName} label="Country" onChange={handleSelectCountry}>
                                                {countriesList.map(item => (
                                                    <MenuItem key={item.isoCode} value={item.isoCode}>
                                                        {item.name}
                                                    </MenuItem>
                                                ))}
                                            </Select> */}
                                            <SearchableDropdown
                                                id="countryId"
                                                name="country"
                                                label="Country"
                                                placeholder="Search country"
                                                size="small"
                                                value={formData.countryName}
                                                handleChange={handleSelectCountry}
                                                dropDownList={countriesList}
                                                dropDownValueKey="name"
                                                valueKey="isoCode"
                                                showAllOption={false}
                                            />
                                            <FormHelperText>{errors.countryName}</FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    <Grid item md={3.9}>
                                        <FormControl fullWidth error={Boolean(errors.state)}>
                                            {/* <InputLabel>State</InputLabel>
                                            <Select value={formData.state} label="State" onChange={handleSelectState}>
                                                {stateList.map(item => (
                                                    <MenuItem key={item.isoCode} value={item.isoCode}>
                                                        {item.name}
                                                    </MenuItem>
                                                ))}
                                            </Select> */}
                                            <SearchableDropdown
                                                id="state"
                                                name="state"
                                                label="State"
                                                placeholder="Search State"
                                                size="small"
                                                value={formData.state}
                                                handleChange={handleSelectState}
                                                dropDownList={stateList}
                                                dropDownValueKey="name"
                                                valueKey="isoCode"
                                                showAllOption={false}
                                            />
                                            <FormHelperText>{errors.state}</FormHelperText>
                                        </FormControl>
                                    </Grid>

                                    <Grid item md={3.9}>
                                        <FormControl fullWidth error={Boolean(errors.city)}>
                                            {/* <InputLabel>City</InputLabel>
                                            <Select
                                                value={formData.city}
                                                label="City"
                                                onChange={e => setFormData(prev => ({ ...prev, city: e.target.value }))}
                                            >
                                                {cityList.map(item => (
                                                    <MenuItem key={item.name} value={item.name}>
                                                        {item.name}
                                                    </MenuItem>
                                                ))}
                                            </Select> */}
                                            <SearchableDropdown
                                                id="city"
                                                name="city"
                                                label="City"
                                                placeholder="Search city"
                                                size="small"
                                                value={formData.city}
                                                handleChange={e => setFormData(prev => ({ ...prev, city: e.target.value }))}
                                                dropDownList={cityList}
                                                dropDownValueKey="name"
                                                valueKey="name"
                                                showAllOption={false}
                                            />
                                            <FormHelperText>{errors.city}</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Grid width="49%" container sx={{ justifyContent: "space-between" }}>
                                    <Grid item md={2}>
                                        <TextField
                                            fullWidth
                                            name="countryCode"
                                            label="Code"
                                            value={formData.countryCode}
                                            onChange={handleChange}
                                            error={Boolean(errors.countryCode)}
                                            helperText={errors.countryCode}
                                            disabled={true}
                                        />
                                    </Grid>
                                    <Grid item md={9.9}>
                                        <TextField
                                            fullWidth
                                            name="mobileNumber"
                                            label={
                                                <span>
                                                    Mobile Number <span style={{ color: "red" }}>*</span>
                                                </span>
                                            }
                                            value={formData.mobileNumber}
                                            onChange={onChangeMobileNumber}
                                            error={Boolean(errors.mobileNumber)}
                                            helperText={errors.mobileNumber}
                                            disabled={!countryShortCode}
                                            type="tel"
                                            inputProps={{ minLength: 7, maxLength: 15, inputMode: "numeric", pattern: "[0-9]*" }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Grid container spacing={2} alignItems="center" className="field-spacing">
                                {/* Password Field (Read-only) */}
                                <Grid item xs={12} md={5}>
                                    <FormControl fullWidth>
                                        <TextField
                                            name="generatePassword"
                                            id="generate-password"
                                            label={
                                                <span>
                                                    Password <span style={{ color: "red" }}>*</span>
                                                </span>
                                            }
                                            variant="outlined"
                                            value={password}
                                            error={Boolean(errors.password)}
                                            disabled
                                            InputProps={{
                                                endAdornment: password && (
                                                    <InputAdornment position="end">
                                                        <CopyToClipboard text={password} onCopy={handleCopyPassword}>
                                                            <Tooltip title={copySuccess ? "Copied!" : "Copy to clipboard"}>
                                                                <IconButton size="small">
                                                                    <ContentCopyIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </CopyToClipboard>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Generate Button */}
                                <Grid item xs={12} md={3}>
                                    <Button fullWidth variant="outlined" onClick={handleGeneratePassword} sx={{ height: "100%" }}>
                                        Generate Password
                                    </Button>
                                </Grid>
                            </Grid>

                            <Grid className="d-flex">
                                <Grid item>
                                    <Button variant="contained" color="error" onClick={onClickCancelButton} className="m-2">
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button variant="contained" onClick={handleCreateSuperAdmin} className="m-2">
                                        Add Super Admin
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    <AlertMessageComponent
                        message={errorMessage}
                        fieldName={"errorMessage"}
                        handleClose={() => setErrorMessage("")}
                        show={Boolean(errorMessage)}
                    />
                </form>
            )}
        </>
    );
}
